import { createSearchParams, RouteMatch } from "react-router-dom";

const Routes = {
  pageNotFound: "*",
  whatsNew: "/whats-new",
  unauthorized: "/unauthorized",
  eventsAndPromotions: "/player/:playerId/promotionsAndEvents",
  comps: "/player/:playerId/comps",
  trips: "/player/:playerId/trips",
  offers: "/player/:playerId/offers",
  credits: "/player/:playerId/credit",
  tasks: "/player/:playerId/tasks",
  notes: "/player/:playerId/notes",
  viewNote: "/player/:playerId/notes/:noteId",
  stays: "/player/:playerId/stays",
  currentStays: "/player/:playerId/stays/current",
  previousStays: "/player/:playerId/stays/previous",
  upcomingStays: "/player/:playerId/stays/upcoming",
  cancelledStays: "/player/:playerId/stays/cancelled",
  viewStay: "/player/:playerId/stays/:stayId/details",
  player: "/player/:playerId",
  overview: "/player/:playerId/overview",
  booking: "/player/:playerId/booking",
  dashboard: "/dashboard",
  birthdays: "/dashboard/host/birthdays",
  reservations: "dashboard/host/reservations",
  activeReservations: "/dashboard/host/reservations/active",
  upcomingReservations: "/dashboard/host/reservations/upcoming",
  activePlayersOnFloor: "/dashboard/host/players/active",
  activeUnHostedPlayersOnFloor: "/dashboard/host/players/active/unhosted",
  hostPromotions: "/dashboard/host/players/calendar/promotions",
  hostEvents: "/dashboard/host/players/calendar/events",
  viewHostEvent: "/dashboard/host/players/calendar/events/:eventId",
  viewHostPromotion: "/dashboard/host/players/calendar/promotions/:eventId",
  viewHostDashboardEvent: "/dashboard/host/events/:eventId",
  viewHostDashboardPromotion: "/dashboard/host/promotions/:eventId",
  viewBuildUpStatusDashboard: "/dashboard/players/buildup/:playerId",
  viewBuildUpStatusPage: "/dashboard/players/page/buildup/:playerId",
  viewFreePlayTodayDashboard: "/dashboard/players/buildup/freePlayToday/:playerId",
  viewFreePlayTodayPage: "/dashboard/players/page/buildup/freePlayToday/:playerId",
  viewBuildUpPlayers: "/dashboard/players/page/buildup",
  guestAndHostAudit: "/dashboard/host/audit/players",
  tripsIncreasing: "/dashboard/host/players/trips/increasing",
  tripsDecreasing: "/dashboard/host/players/trips/decreasing",
  trendingCustomerValues: "/dashboard/host/players/customerscores",
  playersOfInterest: "/dashboard/host/players/favorites",
  playerSearch: "/player/search",
  mobileApp: "/player/:playerId/app",
  netRevenue: "/player/:playerId/netRevenue",
  root: "/",
  previous: ".",
  promosAndEventsFor: (id: number | string) => Routes.eventsAndPromotions.replace(":playerId", id.toString()),
  forHostDashboardEvent: (id: number | string) => Routes.viewHostDashboardEvent.replace(":eventId", id.toString()),
  forHostDashboardPromotion: (id: number | string) =>
    Routes.viewHostDashboardPromotion.replace(":eventId", id.toString()),
  forHostEvent: (id: number | string) => Routes.viewHostEvent.replace(":eventId", id.toString()),
  forHostPromotion: (id: number | string) => Routes.viewHostPromotion.replace(":eventId", id.toString()),
  buildUpStatusDashboardFor: (id: number | string) =>
    Routes.viewBuildUpStatusDashboard.replace(":playerId", id.toString()),
  buildUpStatusPageFor: (id: number | string) => Routes.viewBuildUpStatusPage.replace(":playerId", id.toString()),
  freePlayTodayDashboardFor: (id: number | string) =>
    Routes.viewFreePlayTodayDashboard.replace(":playerId", id.toString()),
  freePlayTodayPageFor: (id: number | string) => Routes.viewFreePlayTodayPage.replace(":playerId", id.toString()),
  stayFor: (playerId: number | string, confirmationNumber: string) =>
    Routes.viewStay.replace(":playerId", playerId.toString()).replace(":stayId", confirmationNumber),
  noteFor: (playerId: number | string, noteId: number | string) =>
    Routes.viewNote.replace(":playerId", playerId.toString()).replace(":noteId", noteId.toString()),
  bookingFor: (playerId: number | string) => Routes.booking.replace(":playerId", playerId.toString()),
  offersFor: (playerId: number | string) => Routes.offers.replace(":playerId", playerId.toString()),
  compsFor: (playerId: number | string) => Routes.comps.replace(":playerId", playerId.toString()),
  creditsFor: (playerId: number | string) => Routes.credits.replace(":playerId", playerId.toString()),
  tripsFor: (playerId: number | string) => Routes.trips.replace(":playerId", playerId.toString()),
  notesFor: (playerId: number | string) => Routes.notes.replace(":playerId", playerId.toString()),
  netRevenueFor: (playerId: number | string, period: string) => `/player/${playerId}/netRevenue?period=${period}`,
  overviewPathFor: (playerId: number | string) => `/player/${playerId}`,
  overviewStatsPathFor: (period: string) => ({
    search: createSearchParams({
      period,
    }).toString(),
  }),
  searchResultPathFor: (term: string) => ({
    pathname: Routes.playerSearch,
    search: createSearchParams({
      term,
    }).toString(),
  }),
  playerOverviewForSearchResult: (playerId: number) => `/player/${playerId}`,
  tabForPlayer: (url: String) => {
    return url.replace(Routes.player + "/", "");
  },
  tabsForStays: (url: String) => {
    return url.replace(Routes.stays + "/", "");
  },
} as const;

export const PagePathes = [
  Routes.unauthorized,
  Routes.eventsAndPromotions,
  Routes.comps,
  Routes.trips,
  Routes.offers,
  Routes.credits,
  Routes.tasks,
  Routes.notes,
  Routes.viewNote,
  Routes.hostEvents,
  Routes.viewHostEvent,
  Routes.stays,
  Routes.currentStays,
  Routes.previousStays,
  Routes.upcomingStays,
  Routes.cancelledStays,
  Routes.viewStay,
  Routes.player,
  Routes.overview,
  Routes.booking,
  Routes.dashboard,
  Routes.birthdays,
  Routes.reservations,
  Routes.activeReservations,
  Routes.upcomingReservations,
  Routes.activePlayersOnFloor,
  Routes.activeUnHostedPlayersOnFloor,
  Routes.playerSearch,
  Routes.hostEvents,
  Routes.hostPromotions,
  Routes.guestAndHostAudit,
  Routes.tripsIncreasing,
  Routes.tripsDecreasing,
  Routes.trendingCustomerValues,
  Routes.root,
  Routes.mobileApp,
  Routes.netRevenue,
  Routes.viewBuildUpPlayers,
  Routes.playersOfInterest,
];

const defaultPageTitle = "Patron 360";

const playerPageTitle = (title: string): ((r: RouteMatch) => string) => {
  return (r) => `${r.params["playerId"]} | ${title}`;
};

export const PageTitles: {
  [key: string]: string | ((r: RouteMatch) => string);
} = {
  [`${Routes.unauthorized}`]: defaultPageTitle,
  [`${Routes.eventsAndPromotions}`]: playerPageTitle("Promotions"),
  [`${Routes.comps}`]: playerPageTitle("Comps"),
  [`${Routes.trips}`]: playerPageTitle("Trips"),
  [`${Routes.offers}`]: playerPageTitle("Offers"),
  [`${Routes.credits}`]: playerPageTitle("Credit"),
  [`${Routes.netRevenue}`]: playerPageTitle("Net Rev"),
  [`${Routes.tasks}`]: playerPageTitle("Tasks"),
  [`${Routes.notes}`]: playerPageTitle("Notes"),
  [`${Routes.viewNote}`]: playerPageTitle("Note"),
  [`${Routes.stays}`]: playerPageTitle("Stays"),
  [`${Routes.currentStays}`]: playerPageTitle("Current Stays"),
  [`${Routes.previousStays}`]: playerPageTitle("Previous Stays"),
  [`${Routes.upcomingStays}`]: playerPageTitle("Upcoming Stays"),
  [`${Routes.cancelledStays}`]: playerPageTitle("Cancelled Stays"),
  [`${Routes.viewStay}`]: playerPageTitle("Stay"),
  [`${Routes.player}`]: playerPageTitle("Overview"),
  [`${Routes.overview}`]: playerPageTitle("Overview"),
  [`${Routes.booking}`]: playerPageTitle("Hotel Booking"),
  [`${Routes.mobileApp}`]: playerPageTitle("Mobile App"),
  [`${Routes.dashboard}`]: "Dashboard",
  [`${Routes.birthdays}`]: "Player Birthdays",
  [`${Routes.reservations}`]: "Player Reservations",
  [`${Routes.activeReservations}`]: "Player Active Reservations",
  [`${Routes.upcomingReservations}`]: "Player Upcoming Reservations",
  [`${Routes.activePlayersOnFloor}`]: "Players Active on Floor",
  [`${Routes.activeUnHostedPlayersOnFloor}`]: "Hot Players on Floor",
  [`${Routes.tripsIncreasing}`]: "Trips Increasing",
  [`${Routes.tripsDecreasing}`]: "Trips Decreasing",
  [`${Routes.trendingCustomerValues}`]: "Trending Customer Values",
  [`${Routes.hostEvents}`]: "Events",
  [`${Routes.viewHostEvent}`]: "Event",
  [`${Routes.hostPromotions}`]: "Promotions",
  [`${Routes.guestAndHostAudit}`]: "Guest and Host Audit",
  [`${Routes.playerSearch}`]: "Search Results",
  [`${Routes.netRevenue}`]: "Net Revenue",
  [`${Routes.viewBuildUpPlayers}`]: "Flagged Free Play",
  [`${Routes.playersOfInterest}`]: "Players of Interest",
  [`${Routes.root}`]: defaultPageTitle,
};

export const getPageTitle = (route: RouteMatch) => {
  const routePath = route.route.path;
  if (!routePath) return defaultPageTitle;
  const title = PageTitles[routePath as (typeof PagePathes)[number]];
  if (!title) return defaultPageTitle;
  if (typeof title === "string") return title;
  return title(route);
};

export default Routes;
